/* eslint-disable react-hooks/exhaustive-deps */
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { PRODUCT_TYPE } from "common";
import { FormTextField, GridCardContent } from "components/atoms";
import { FormContainer, FormContent } from "components/molecules";
import { FormikContextType, FormikHelpers, useFormik } from "formik";
import { DialogHelper, RoleHelper, StringHelper, TableHelper } from "helpers";
import { useRequest } from "hooks/useRequest";
import { difference, every, find, map } from "lodash";
import {
  EPackagingType,
  ICreateProductPacking,
  IGetByIdRequest,
  IGetProductPacking,
  IProductPacking,
  IUpdateProductPackingRequest,
  IProduct,
} from "model";
import {
  ICreateProductRequest,
  IUpdateProductRequest,
} from "model/Product/IProduct";
import BasePage from "pages/base";
import { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { MasterDataActions, ProductActions } from "redux/actions";
import configureStore from "redux/configurations/configureStore";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import ProductApiService from "services/ProductApiService";
import { theme } from "themes";
import { I18nTranslator, translations } from "utils/i18n";
import * as yup from "yup";
import { GroupSelect } from "./components/GroupSelect";

enum EStatus {
  ACTIVE = "A",
  IN_ACTIVE = "I",
}

enum EHasQRCode {
  QR_CODE = "A",
  NO_QR_CODE = "I",
}

const PACKAGING_TYPE = [
  {
    type: EPackagingType.CARTON,
    name: "Thùng carton",
  },
  {
    type: EPackagingType.SHOVE,
    name: "Xô",
  },
  {
    type: EPackagingType.BAG,
    name: "Túi",
  },
  {
    type: EPackagingType.CAN,
    name: "Can",
  },
];

const validateSchema = yup.object().shape({
  description: yup.string().required(translations.yupError.required),
  productCode: yup
    .string()
    .required(translations.yupError.required)
    .matches(/^([A-Z,a-z,0-9])+$/, translations.yupError.formatNotValid),
  packingType: yup.string().required(translations.yupError.required),
  type: yup.string().required(translations.yupError.required),
  uom: yup.string().required(translations.yupError.required),
  quantityByUnit: yup.number().min(0).required(translations.yupError.required),
  quantityByCarton: yup
    .number()
    .min(0)
    .required(translations.yupError.required),
  hasQRCode: yup.string().required(translations.yupError.required),
});

const ProductDetail = () => {
  const location = useLocation();
  const history = useHistory();
  const params = useParams<{ id: string }>();

  const dispatch = useAppDispatch();
  const isAdd = location.pathname.includes("new");
  const action = useAppSelector((state) => state.ProductReducer.action);
  const [getProductByIdRequest] = useState({ id: params.id });

  const { data } = useRequest<IGetByIdRequest, IProduct>(
    getProductByIdRequest,
    ProductApiService.getProductById,
    [],
    {
      holdRequest: isAdd,
    }
  );

  const initialValues = isAdd
    ? ({
        description: "",
        productCode: "",
        productCodeOld: "",
        packingType: "",
        type: "",
        uom: "",
        quantityByUnit: 0,
        quantityByCarton: 0,
        heatProtection: "",
        categoryId: "",
        hasQRCode: "",
        status: EStatus.ACTIVE,
        groupId: "",
      } as ICreateProductRequest)
    : ({
        ...data,
        hasQRCode: data?.hasQRCode ? EHasQRCode.QR_CODE : EHasQRCode.NO_QR_CODE,
        status: data?.status ? EStatus.ACTIVE : EStatus.IN_ACTIVE,
      } as IUpdateProductRequest);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: validateSchema,
    onSubmit: (
      values: ICreateProductRequest | IUpdateProductRequest,
      { validateForm }: FormikHelpers<any>
    ) => {
      validateForm().then(() => {
        const createRequest: ICreateProductRequest = {
          productCode: values.productCode,
          description: values.description,
          type: values.type,
          uom: values.uom,
          packingType: values.packingType,
          heatProtection: values.heatProtection,
          quantityByUnit: values.quantityByUnit,
          quantityByCarton: values.quantityByCarton,
          hasQRCode: values.hasQRCode === EHasQRCode.QR_CODE,
          status: values.status === EStatus.ACTIVE,
          categoryId: "00000000-0000-0000-0000-000000000000",
        };
        if (isAdd) {
          dispatch(ProductActions.createProduct.request(createRequest));
        } else {
          const data = values as IUpdateProductRequest;
          const updateRequest: IUpdateProductRequest = {
            id: data.id,
            productCode: data.productCode,
            productCodeOld: data.productCodeOld,
            description: data.description,
            categoryId: data.categoryId,
            type: data.type,
            uom: data.uom,
            packingType: data.packingType,
            sampleQty: data.sampleQty,
            printLabelPercentage: data.printLabelPercentage,
            palletSize: data.palletSize,
            quantityByUnit: data.quantityByUnit,
            quantityByCarton: data.quantityByCarton,
            heatProtection: data.productCode,
            hasQRCode: data.hasQRCode === EHasQRCode.QR_CODE,
            status: data.status === EStatus.ACTIVE,
            isDelete: false,
            groupId: data.groupId,
          };
          dispatch(ProductActions.updateProduct.request(updateRequest));
        }
      });
    },
  });

  const createOrUpdateOrDeleteProductSuccessfully =
    action.includes(ProductActions.createProduct.successName) ||
    action.includes(ProductActions.updateProduct.successName) ||
    action.includes(ProductActions.deleteProduct.successName);

  const handleDeleteItem = () => {
    DialogHelper.openConfirmDeleteDialog({
      itemName: data?.description,
      onDelete: () => _deleteProduct(data?.id!),
    });
  };

  const getCardTitle = () => {
    return isAdd
      ? translations.masterData.addProduct
      : translations.masterData.updateProduct;
  };

  const _clearReducer = () => {
    dispatch(ProductActions.clearReducer.request());
  };
  const _deleteProduct = (id: string) => {
    dispatch(
      ProductActions.deleteProduct.request({
        id: id,
      })
    );
  };

  useEffect(() => {
    return () => {
      _clearReducer();
    };
  }, []);

  useEffect(() => {
    if (createOrUpdateOrDeleteProductSuccessfully) {
      history.go(-1);
    }
  }, [action]);

  return (
    <BasePage loading={action.includes("_REQUEST")}>
      <GridCardContent>
        <Box sx={{ bgcolor: "white", p: 2, height: "100%", width: "100%" }}>
          <FormContainer
            formik={formik}
            loading={action.includes("_REQUEST")}
            isAdd={isAdd}
            onDelete={handleDeleteItem}
            title={I18nTranslator(getCardTitle())}
          >
            <FormGeneralInformation isAdd={isAdd} />
            {!isAdd && <SpecificationsProduct formik={formik} />}
          </FormContainer>
        </Box>
      </GridCardContent>
    </BasePage>
  );
};

const FormGeneralInformation = ({ isAdd }: { isAdd: boolean }) => {
  const dispatch = useAppDispatch();
  // const productCategory = useAppSelector(
  //   (state) => state.MasterDataReducer.productCategory
  // );
  const unitMeasure = useAppSelector(
    (state) => state.MasterDataReducer.unitMeasure
  );
  const zoneTemperature = useAppSelector(
    (state) => state.MasterDataReducer.zoneTemperature
  );
  useEffect(() => {
    _getProductCategories();
    _getUnitMeasure();
    _getZoneTemperature();
  }, []);

  const _getProductCategories = () => {
    dispatch(MasterDataActions.getProductCategories.request());
  };
  const _getUnitMeasure = () => {
    dispatch(MasterDataActions.getUnitMeasure.request());
  };
  const _getZoneTemperature = () => {
    dispatch(MasterDataActions.getZoneTemperature.request());
  };

  // const renderProductCategoriesMenu = () => {
  //   return productCategory.data ? (
  //     productCategory.data.map((category, index) => {
  //       return (
  //         <MenuItem key={index.toString()} value={category.id}>
  //           <Typography color={"black"}>{category.description}</Typography>
  //         </MenuItem>
  //       );
  //     })
  //   ) : (
  //     <Typography>No item</Typography>
  //   );
  // };
  const renderUnitMeasureMenu = () => {
    return unitMeasure.data ? (
      unitMeasure.data.map((unit, index) => {
        return (
          <MenuItem key={index.toString()} value={unit.uom}>
            <Typography color={"black"}>{unit.description}</Typography>
          </MenuItem>
        );
      })
    ) : (
      <Typography>No item</Typography>
    );
  };
  const renderZoneTemperatureMenu = () => {
    return zoneTemperature.data ? (
      zoneTemperature.data.map((temperature, index) => {
        return (
          <MenuItem key={index.toString()} value={temperature.refValue}>
            <Typography color={"black"}>{temperature.refName}</Typography>
          </MenuItem>
        );
      })
    ) : (
      <Typography>{I18nTranslator(translations.masterData.noItem)}</Typography>
    );
  };

  return (
    <FormContent
      title={I18nTranslator(translations.masterData.generaInformation)}
    >
      <Grid xs={6} item>
        <FormTextField
          required
          // InputProps={{ type: "number" }}
          label={I18nTranslator(translations.masterData.productCode)}
          placeholder={I18nTranslator(translations.placeholder.productCode)}
          id="productCode"
        />
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          required
          label={I18nTranslator(translations.masterData.productName)}
          placeholder={I18nTranslator(translations.placeholder.productName)}
          id="description"
        />
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          required
          label={I18nTranslator(translations.masterData.productType)}
          placeholder={I18nTranslator(translations.masterData.productType)}
          select
          id="type"
        >
          {PRODUCT_TYPE.map((type, index) => {
            if (type.type && index !== 0) {
              return (
                <MenuItem value={type.type}>
                  <Typography color={"black"}>
                    {StringHelper.getProductTypeName(type.type)}
                  </Typography>
                </MenuItem>
              );
            } else {
              return null;
            }
          })}
        </FormTextField>
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          label={I18nTranslator(translations.masterData.massPerUnit)}
          type="number"
          InputProps={{
            endAdornment: <InputAdornment position="end">Kg</InputAdornment>,
          }}
          id="quantityByUnit"
        />
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          required
          label={I18nTranslator(translations.masterData.unitOfMeasure)}
          placeholder={I18nTranslator(translations.masterData.unitOfMeasure)}
          select
          id="uom"
        >
          {unitMeasure.action.includes("_REQUEST") ? (
            <CircularProgress size={20} />
          ) : (
            renderUnitMeasureMenu()
          )}
        </FormTextField>
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          label={I18nTranslator(translations.masterData.massPerCarton)}
          type="number"
          InputProps={{
            endAdornment: <InputAdornment position="end">Kg</InputAdornment>,
          }}
          id="quantityByCarton"
        />
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          required
          label={I18nTranslator(translations.masterData.packagingType)}
          placeholder={I18nTranslator(translations.masterData.packagingType)}
          select
          id="packingType"
        >
          {PACKAGING_TYPE.map((type) => {
            return (
              <MenuItem value={type.type}>
                <Typography color={"black"}>
                  {StringHelper.getProductPackagingName(type.type)}
                </Typography>
              </MenuItem>
            );
          })}
        </FormTextField>
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          label={I18nTranslator(translations.masterData.storageTemperatures)}
          placeholder={I18nTranslator(
            translations.placeholder.storageTemperatures
          )}
          select
          id="heatProtection"
        >
          {zoneTemperature.action.includes("_REQUEST") ? (
            <CircularProgress size={20} />
          ) : (
            renderZoneTemperatureMenu()
          )}
        </FormTextField>
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          required
          label={I18nTranslator(translations.masterData.qrCode)}
          select
          id="hasQRCode"
        >
          <MenuItem value={EHasQRCode.QR_CODE}>
            {I18nTranslator(translations.masterData.qrCode)}
          </MenuItem>
          <MenuItem value={EHasQRCode.NO_QR_CODE}>
            {I18nTranslator(translations.masterData.noQRCode)}
          </MenuItem>
        </FormTextField>
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          label={I18nTranslator(translations.masterData.status)}
          select
          id="status"
        >
          <MenuItem value={EStatus.ACTIVE}>
            {I18nTranslator(translations.masterData.active)}
          </MenuItem>
          <MenuItem value={EStatus.IN_ACTIVE}>
            {I18nTranslator(translations.masterData.inactive)}
          </MenuItem>
        </FormTextField>
      </Grid>
      <Grid xs={6} item>
        <GroupSelect />
      </Grid>
      {!isAdd && (
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.masterData.productCodeOld)}
            placeholder={I18nTranslator(
              translations.placeholder.productCodeOld
            )}
            id="productCodeOld"
          />
        </Grid>
      )}
    </FormContent>
  );
};

const TYPES = [
  {
    name: I18nTranslator(translations.masterData.pallet),
    level: 0,
  },
  { name: I18nTranslator(translations.masterData.carton1), level: 1 },
  { name: I18nTranslator(translations.masterData.carton2), level: 2 },
  { name: I18nTranslator(translations.masterData.carton3), level: 3 },
  { name: I18nTranslator(translations.masterData.carton4), level: 4 },
];
const SpecificationsProduct = (props: { formik: FormikContextType<any> }) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isAdd = location.pathname.includes("new");
  const [packaging, setPackaging] = useState<IProductPacking[]>([]);
  const [refresh, setRefresh] = useState<boolean>(false);

  const params = useParams<{ id: string }>();
  const [request] = useState({
    productId: params.id,
    pageNumber: 1,
    pageSize: 100,
  });

  const action = useAppSelector((state) => state.ProductReducer.action);
  const { loading, data } = useRequest<IGetProductPacking, IProductPacking[]>(
    request,
    ProductApiService.getProductPacking,
    [],
    {
      refresh: refresh,
      holdRequest: isAdd,
    },
    () => {
      setRefresh(false);
    }
  );
  const _clearReducer = () => {
    dispatch(ProductActions.clearReducer.request());
  };

  //initial check
  useEffect(() => {
    setPackaging(isAdd ? [] : data || []);
  }, [data]);

  //initial check
  useEffect(() => {
    if (action.includes(ProductActions.updateProductPacking.successName)) {
      setRefresh(true);
      _clearReducer();
    }
  }, [action]);

  const handleCreateProductPacking = (row: ICreateProductPacking) => {
    const request: IUpdateProductPackingRequest = {
      productPacking: [
        {
          productId: params.id,
          level: row.level,
          productPackingCode: "",
          quantity: row.quantity,
          type: row.type,
          warehouseId:
            configureStore().store.getState().IdentityReducer.currentWarehouse
              ?.id,
          isDelete: false,
        },
      ],
    };
    _updateProductPacking(request);
  };
  const handleUpdateProductPacking = (row: IProductPacking) => {
    const request: IUpdateProductPackingRequest = {
      productPacking: [
        {
          id: row.id,
          productId: params.id,
          level: row.level,
          productPackingCode: row.productPackingCode,
          quantity: row.quantity,
          type: row.type,
          warehouseId: row.warehouseId,
          isDelete: false,
        },
      ],
    };
    _updateProductPacking(request);
  };

  const handleDeleteProductPacking = (row: IProductPacking) => {
    const request: IUpdateProductPackingRequest = {
      productPacking: [
        {
          id: row.id,
          productId: row.productId,
          level: row.level,
          productPackingCode: row.productPackingCode,
          quantity: row.quantity,
          type: row.type,
          warehouseId: row.warehouseId,
          isDelete: true,
        },
      ],
    };
    _updateProductPacking(request);
  };

  const _updateProductPacking = (request: IUpdateProductPackingRequest) => {
    dispatch(ProductActions.updateProductPacking.request(request));
  };

  const columns: GridColDef[] = [
    TableHelper.renderSTT(),
    {
      field: "type",
      headerName: I18nTranslator(translations.receive.type),
      width: 200,
      type: "singleSelect",
      valueOptions: map(
        TYPES.filter((type) => !map(packaging, "level").includes(type.level)),
        "name"
      ),
      editable: true,
      renderCell: ({ row }) => {
        return (
          <Typography>
            {row.type === "P"
              ? I18nTranslator(translations.masterData.pallet)
              : `${I18nTranslator(translations.masterData.cartonLevel)} ${
                  row.level
                }`}
          </Typography>
        );
      },
    },

    {
      field: "quantity",
      headerName: I18nTranslator(translations.masterData.specification),
      width: 150,
      editable: true,
      type: "number",
    },

    {
      field: "action",
      headerName: I18nTranslator(translations.masterData.action),
      width: 150,
      renderCell: ({ row }) => {
        return (
          <IconButton
            onClick={() => handleDeleteProductPacking(row)}
            disabled={!RoleHelper.isSuperAdmin()}
          >
            <DeleteIcon
              sx={{ color: theme.colors.error.light }}
              fontSize="small"
            />
          </IconButton>
        );
      },
    },
  ];

  const handleAddNew = () => {
    if (packaging.length < TYPES.length) {
      const getNewLevel = () => {
        switch (true) {
          case packaging.length === 0:
            return {
              type: "C",
              level: 1,
            };
          case every(packaging, { type: "C" }):
            return {
              type: "P",
              level: 0,
            };
          default:
            const levels = difference([1, 2, 3, 4], map(packaging, "level"));
            return {
              type: "C",
              level: levels[0],
            };
        }
      };

      const request: ICreateProductPacking = {
        type: getNewLevel().type,
        quantity: 0,
        level: getNewLevel().level,
      };
      handleCreateProductPacking(request);
    }
  };

  const handleEditCell = (param: any) => {
    let arr = [...packaging];
    const itemIndex = arr.findIndex((item) => item.id === param.id);
    if (param.field === "type") {
      const getLevel = find(TYPES, (type) => type.name === param.value)?.level!;
      arr[itemIndex].type = param.value?.includes("Pallet") ? "P" : "C";
      arr[itemIndex].level = getLevel;
    } else {
      arr[itemIndex].quantity = param.value;
    }
    handleUpdateProductPacking(arr[itemIndex]);
  };

  const Header = () => {
    return (
      <Box sx={{ p: 2 }}>
        <IconButton
          sx={{ backgroundColor: theme.colors.primary.main }}
          onClick={handleAddNew}
          disabled={!RoleHelper.isSuperAdmin()}
        >
          <AddIcon sx={{ color: "white" }} fontSize={"small"} />
          <Typography
            sx={{ paddingLeft: "6px", color: "white" }}
            fontWeight={"500"}
          >
            {I18nTranslator(translations.button.addNew)}
          </Typography>
        </IconButton>
      </Box>
    );
  };
  return (
    <FormContent title={I18nTranslator(translations.receive.productPackaging)}>
      <Grid
        item
        xs={12}
        style={{
          height: 400,
          marginTop: "10px",
          width: "100%",
        }}
      >
        <DataGrid
          loading={loading}
          rows={packaging.map((packing, index) => {
            return {
              ...packing,
              no: index + 1,
            };
          })}
          columns={columns}
          components={{
            Toolbar: Header,
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {I18nTranslator(translations.table.noData)}
              </Stack>
            ),
          }}
          hideFooter
          hideFooterPagination
          hideFooterSelectedRowCount
          onCellEditCommit={handleEditCell}
        />
      </Grid>
    </FormContent>
  );
};

export default ProductDetail;
